import React from 'react';

import { Formik, Form as FormikForm } from 'formik';

import { Modal } from '@shared/components/bootstrap';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikMultiselectFormGroup } from '@shared/components/fields/formik/formik_multiselect_form_group';

import { client } from '@admin/libraries/apollo';
import { Status, useFeatureFlagUpsertMutation, FeatureFlagInput } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ErrorBanner } from '@admin/styled/error_banner';
import { FormBody } from '@admin/styled/form_body';
import { FormCard } from '@admin/styled/form_card';
import { FormFooter } from '@admin/styled/form_footer';

type IDWithName = {
  id: string;
  name: string;
};

export type FeatureFlagType = {
  id: string;
  name: string;
  description?: string | null;
  resourceName: string;
  whitelistedObjects: IDWithName[];
};

export const UpsertModal: React.FC<{
  featureFlag?: FeatureFlagType;
  regionOptions: IDWithName[];
  userOptions: IDWithName[];
  warehouseOptions: IDWithName[];
  closeModal(): void;
}> = ({ featureFlag, regionOptions, userOptions, warehouseOptions, closeModal }) => {
  const [save, { loading, data }] = useFeatureFlagUpsertMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.status === Status.Ok) closeModal();
    },
  });

  const onSubmit = (input: FeatureFlagInput) => {
    save({
      variables: {
        id: featureFlag?.id,
        input,
      },
    });
  };

  let OPTIONS: IDWithName[] = [];
  if (featureFlag?.resourceName === 'Region') {
    OPTIONS = regionOptions.map((option) => ({ id: option.id, name: `${option.id} - ${option.name}` }));
  } else if (featureFlag?.resourceName === 'User') {
    OPTIONS = userOptions.map((option) => ({ id: option.id, name: `${option.id} - ${option.name}` }));
  } else if (featureFlag?.resourceName === 'Warehouse') {
    OPTIONS = warehouseOptions.map((option) => ({ id: option.id, name: `${option.id} - ${option.name}` }));
  } else {
    OPTIONS = [];
  }

  const whitelistEditingDisabled = OPTIONS.length === 0;

  return (
    <Modal onClose={closeModal}>
      <Formik<FeatureFlagInput>
        initialValues={{
          name: featureFlag?.name ?? '',
          description: featureFlag?.description ?? '',
          resourceName: featureFlag?.resourceName ?? '',
          whitelistedIDs: featureFlag?.whitelistedObjects.map((object) => object.id) ?? [],
        }}
        onSubmit={onSubmit}
      >
        <FormikForm>
          <FormCard>
            <FormBody>
              {data?.result?.error && <ErrorBanner role="alert">{data.result.error}</ErrorBanner>}
              {whitelistEditingDisabled && (
                <ErrorBanner role="alert">
                  This feature flag is not editable via Teal. Please submit a tech support ticket with the requested
                  changes.
                </ErrorBanner>
              )}
              <FormikInputFormGroup label="Name" name="name" disabled={true} />
              <FormikInputFormGroup label="Resource Name" name="resourceName" disabled={true} />
              <FormikInputFormGroup label="Description" name="description" disabled={false} />
              <FormikMultiselectFormGroup
                label="Whitelist"
                name="whitelistedIDs"
                entries={OPTIONS}
                disabled={whitelistEditingDisabled}
              />
            </FormBody>
            <FormFooter>
              <Button variant="secondary" type="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading || whitelistEditingDisabled}>
                {loading ? 'Saving…' : 'Save'}
              </Button>
            </FormFooter>
          </FormCard>
        </FormikForm>
      </Formik>
    </Modal>
  );
};
